import type { TFunction } from 'i18next';
import i18n from '@/translations/config';
import nlCommon from '@/translations/nl/common.json';
import frCommon from '@/translations/fr/common.json';

export type Language = 'en' | 'es' | 'pl' | 'nl' | 'fr' | 'de' | 'it';

const nlBundle = nlCommon as Record<string, unknown>;
const frBundle = frCommon as Record<string, unknown>;

/** Dutch breed descriptions – used when nl bundle lookup fails so description always translates. */
const NL_BREED_DESCRIPTIONS: Record<string, string> = {
  beagle:
    'Vriendelijk en nieuwsgierig. Beagles zijn kleine tot middelgrote jachthonden met een uitstekend reukvermogen en een vriendelijk karakter.',
  labradoodle:
    'Energiek en vriendelijk. Labradoodles werden oorspronkelijk gefokt als hypoallergene assistentiehonden.',
  'bernese-mountain-dog':
    'Zacht en kalm. Berner Sennenhonden zijn grote, driekleurige werkende honden met een zachtaardig temperament.',
};

/** French breed descriptions – used when fr bundle lookup fails so description always translates. */
const FR_BREED_DESCRIPTIONS: Record<string, string> = {
  beagle:
    'Amical et curieux. Les beagles sont des chiens de chasse de petite à moyenne taille avec un excellent odorat et un caractère amical.',
  labradoodle:
    "Énergique et amical. Les labradoodles ont été élevés à l'origine comme chiens d'assistance hypoallergéniques.",
  'bernese-mountain-dog':
    'Doux et calme. Les bouviers bernois sont de grands chiens de travail tricolores au tempérament doux.',
};

export function getBreedDescription(
  language: Language,
  breedId: string,
  fallback: string,
  t?: TFunction
): string {
  if (language === 'en') return fallback;
  const id = breedId.trim().toLowerCase().replace(/\s+/g, '-');
  if (language === 'nl') {
    const nl = NL_BREED_DESCRIPTIONS[id];
    if (nl) return nl;
  }
  if (language === 'fr') {
    const fr = FR_BREED_DESCRIPTIONS[id];
    if (fr) return fr;
  }
  return getFindYourPetTranslation(
    `breedDescriptions.${breedId}`,
    language,
    fallback,
    t
  );
}

const VALUE_BASED_PREFIXES = [
  'careTranslations',
  'healthTranslations',
  'lifespanTranslations',
  'frequencyTranslations',
];

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Build the i18n key used for Know Your Pet dynamic content (breed descriptions, traits, care, etc.).
 * Use this key with the component's t() so the current UI language is respected.
 */
export function getKnowYourPetI18nKey(key: string): string {
  const dot = key.indexOf('.');
  if (dot === -1) return `knowYourPet.${key}`;
  const prefix = key.slice(0, dot);
  const suffix = key.slice(dot + 1);
  if (VALUE_BASED_PREFIXES.includes(prefix)) {
    const slug = slugify(suffix);
    return slug ? `knowYourPet.${prefix}.${slug}` : `knowYourPet.${key}`;
  }
  return `knowYourPet.${key}`;
}

function getFromObject(
  bundle: Record<string, unknown>,
  i18nKey: string
): string | undefined {
  const parts = i18nKey.split('.');
  let cur: unknown = bundle;
  for (const p of parts) {
    if (cur === null || cur === undefined || typeof cur !== 'object')
      return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return typeof cur === 'string' ? cur : undefined;
}

function getFromBundle(language: string, i18nKey: string): string | undefined {
  const resources =
    (i18n.options?.resources as Record<
      string,
      { translation?: Record<string, unknown> }
    >) ?? {};
  const bundle = resources[language]?.translation;
  if (!bundle) return undefined;
  return getFromObject(bundle as Record<string, unknown>, i18nKey);
}

/**
 * Resolve a Find Your Pet dataset translation.
 * For Dutch (nl), always reads from the nl bundle first to avoid i18n/t() lookup issues.
 * Otherwise uses t() when provided, then direct bundle read, then getFixedT.
 */
export function getFindYourPetTranslation(
  key: string,
  language: Language,
  fallback: string,
  t?: TFunction
): string {
  if (language === 'en') return fallback;

  const i18nKey = getKnowYourPetI18nKey(key);

  // For Dutch, read from the directly imported NL bundle so we never depend on i18n runtime (fixes nl not translating).
  if (language === 'nl') {
    const direct = getFromObject(nlBundle, i18nKey);
    if (direct !== undefined) return direct;
  }

  // For French, read from the directly imported FR bundle for consistency.
  if (language === 'fr') {
    const direct = getFromObject(frBundle, i18nKey);
    if (direct !== undefined) return direct;
  }

  const direct = getFromBundle(language, i18nKey);
  if (direct !== undefined) return direct;

  if (t) {
    const translated = t(i18nKey, { defaultValue: fallback });
    return translated !== i18nKey ? translated : fallback;
  }

  const tFixed = i18n.getFixedT(language, 'translation');
  const translated = tFixed(i18nKey, { defaultValue: fallback });
  return translated !== i18nKey ? translated : fallback;
}
